import { Link } from "react-router-dom";
import { ShoppingCart, Leaf } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang, useT } from "@/lib/i18n";

export default function Navbar() {
  const { count } = useCart();
  const [store, setStore] = useState({ name: "Saravana Stores", logo_url: "" });
  const headerRef = useRef(null);
  const { lang, setLang } = useLang();
  const t = useT();

  useEffect(() => {
    api.get("/store").then((r) => setStore(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const setVar = () => {
      if (!headerRef.current) return;
      const h = headerRef.current.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--nav-h", `${Math.round(h)}px`);
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener("resize", setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/5"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 md:gap-4 group" data-testid="brand-link">
          {store.logo_url ? (
            <img
              src={store.logo_url}
              alt={store.name}
              className="h-16 sm:h-20 md:h-24 w-auto max-w-[220px] object-contain"
              data-testid="brand-logo"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-forest flex items-center justify-center">
              <Leaf className="w-6 h-6 text-bone" />
            </div>
          )}
          <div className="leading-tight" data-testid="brand-title">
            <div className="font-serif text-lg sm:text-xl md:text-2xl font-bold text-forest tracking-tight">
              Saravana Stores
            </div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.24em] text-moss/80 mt-0.5">
              Farmer's Market
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-3 md:gap-4">
          {/* Language toggle */}
          <div
            className="inline-flex items-center rounded-full border border-black/10 bg-white/80 p-0.5"
            role="group"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
              style={{
                backgroundColor: lang === "en" ? "var(--forest)" : "transparent",
                color: lang === "en" ? "#FFFFFF" : "var(--charcoal)",
              }}
              aria-pressed={lang === "en"}
            >
              EN
            </button>
            <button
              onClick={() => setLang("ta")}
              data-testid="lang-ta"
              className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
              style={{
                backgroundColor: lang === "ta" ? "var(--forest)" : "transparent",
                color: lang === "ta" ? "#FFFFFF" : "var(--charcoal)",
              }}
              aria-pressed={lang === "ta"}
            >
              த
            </button>
          </div>

          <Link
            to="/checkout"
            data-testid="nav-cart-link"
            className="relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 hover:opacity-90 transition-opacity duration-200"
            style={{ backgroundColor: "var(--forest)", color: "#FFFFFF" }}
          >
            <ShoppingCart className="w-4 h-4" style={{ color: "#FFFFFF" }} />
            <span className="text-sm font-semibold" style={{ color: "#FFFFFF" }}>{t("cart")}</span>
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="ml-1 min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold flex items-center justify-center"
                style={{ backgroundColor: "var(--terracotta)", color: "#FFFFFF" }}
              >
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}
